/**
 * network.js — connectivity abstraction.
 *
 * On native, backed by `@capacitor/network` which surfaces real OS-level
 * connectivity (including cellular vs wifi type). On web, backed by
 * `navigator.onLine` + the `online` / `offline` events.
 *
 * Consumers should treat this as a hint, not a promise: WebView
 * connectivity is not the same as API reachability. The active-charging
 * flow still relies on axios errors and SSE `onerror` for authoritative
 * "the API is unreachable" signals — this layer is for banner UI and
 * skipping obviously-doomed retries.
 *
 * Snapshot shape:
 *   {
 *     connected: boolean,        // best-known online state
 *     connectionType: string,    // 'wifi' | 'cellular' | 'none' | 'unknown'
 *   }
 */
import { isNative } from './platform.js';

let _network = null;

async function networkPlugin() {
  if (_network) return _network;
  const mod = await import('@capacitor/network');
  _network = mod.Network;
  return _network;
}

function webSnapshot() {
  if (typeof navigator === 'undefined') {
    return { connected: true, connectionType: 'unknown' };
  }
  return {
    connected: navigator.onLine !== false,
    connectionType: 'unknown',
  };
}

/** Point-in-time connectivity check. Never throws. */
export async function getStatus() {
  if (isNative()) {
    try {
      const net = await networkPlugin();
      const status = await net.getStatus();
      return {
        connected: status.connected === true,
        connectionType: status.connectionType || 'unknown',
      };
    } catch { /* fall through to web shape */ }
  }
  return webSnapshot();
}

/**
 * Subscribe to connectivity changes. Handler receives the same shape
 * as `getStatus()`. Returns an unsubscribe function.
 *
 * On native the subscription is the Capacitor `networkStatusChange`
 * event. On web it's a debounced pair of `online`/`offline` listeners
 * — most browsers fire these liberally (every tab switch on some
 * versions of Safari) so we coalesce transitions.
 */
export function onStatusChange(handler) {
  if (typeof handler !== 'function') return () => {};

  if (isNative()) {
    let cleanup = () => {};
    let cancelled = false;
    (async () => {
      try {
        const net = await networkPlugin();
        const sub = await net.addListener('networkStatusChange', (status) => {
          handler({
            connected: status.connected === true,
            connectionType: status.connectionType || 'unknown',
          });
        });
        if (cancelled) { sub.remove(); return; }
        cleanup = () => { try { sub.remove(); } catch { /* ignore */ } };
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; cleanup(); };
  }

  if (typeof window === 'undefined') return () => {};
  let debounceHandle = null;
  const emit = () => {
    if (debounceHandle) clearTimeout(debounceHandle);
    debounceHandle = setTimeout(() => {
      handler(webSnapshot());
      debounceHandle = null;
    }, 250);
  };
  window.addEventListener('online', emit);
  window.addEventListener('offline', emit);
  return () => {
    if (debounceHandle) clearTimeout(debounceHandle);
    window.removeEventListener('online', emit);
    window.removeEventListener('offline', emit);
  };
}
